import { FC, useState, useRef, useCallback, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { type HTMLMotionProps, motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Confetti from 'react-confetti';
import { type UserOnboardInputV3 } from '@magicbrief/server/src/user/types';
import { TOUR_LINK } from '@magicbrief/common';
import BaseModal from 'src/components/AriaModal/BaseModal';
import { AriaButton } from 'src/components/Button/Button';
import Spinner from 'src/components/Loaders/Spinner';
import Input from 'src/components/Input';
import { useI18nContext } from 'src/i18n/i18n-react';
import { trpc } from 'src/lib/trpc';
import { useFacebook } from 'src/utils/useFacebook';
import Tiktok from 'src/assets/svgicons/brands/TikTokColor.svg';
import MetaLogo from 'src/assets/svgicons/brands/meta.svg';
import ArrowRight from 'src/assets/svgicons/line/arrow-right.svg';
import CheckCircle from 'src/assets/svgicons/solid/check-circle.svg';
import ComingSoonReportingPlatforms from 'src/pages/Insights/routes/InsightsSummarySchedules/components/ComingSoonReportingPlatforms';
import SlackColor from 'src/assets/svgicons/brands/SlackColor.svg';
import useNewAnalyticsEvent from 'src/utils/useNewAnalyticsEvent';
import { InsightsAdAccountSelectorList } from 'src/pages/Insights/components/InsightsAdAccountSelectorList/InsightsAdAccountSelectorList';
import ComingSoonAdAccountPlatforms from '../ComingSoonAdAccountPlatforms/ComingSoonAdAccountPlatforms';
import { useUserAndOrganisation } from '../../utils/useUserAndOrganisation';
import SegmentedProgressBar from '../SegmentedProgressBar';
import { TikTokConnect } from '../TikTokConnect/TikTokConnect';
import Alert from '../AlertV2';
import SlackConnect from '../SlackConnect/SlackConnect';
import { firstStepFields, type OnboardingField } from './constants';

type Props = {
  isNewInvitedUser: boolean;
  onComplete: () => void;
};

const OnboardingModal: FC<Props> = ({ isNewInvitedUser, onComplete }) => {
  const navigate = useNavigate();
  const [show, setShow] = useState(true);
  const [step, setStep] = useState(1);
  const [selectedAdAccountIds, setSelectedAdAccountIds] = useState<string[]>(
    []
  );
  const [selectedPlatform, setSelectedPlatform] = useState<
    'facebook' | 'tiktok'
  >('facebook');
  const [tikTokAuthState, setTikTokAuthState] = useState<'init' | 'login'>(
    'init'
  );
  const [bypassAdConnect, setBypassAdConnect] = useState(false);
  const direction = useRef<-1 | 0 | 1>(0);
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const {
    authenticate: authenticateFacebook,
    authState: facebookAuthState,
    updateIntegration: updateFacebookIntegration,
  } = useFacebook({ invalidateQueries: false });
  const userAndOrg = useUserAndOrganisation();
  const onboard = trpc.user.completeUserOnboardFormV3.useMutation();
  const { LL } = useI18nContext();
  const trpcUtils = trpc.useUtils();

  const tikTokAdvertiserAccounts =
    trpc.tikTok.getCachedAdvertiserAccounts.useQuery(undefined, {
      enabled: false,
    });
  const connectTikTokIntegration =
    trpc.tikTok.connectAdvertiserAccounts.useMutation({
      onError: () => {
        toast.error(LL.tikTok.oauth.exchange.error.body1());
      },
    });
  const { recordEvent } = useNewAnalyticsEvent();

  /**
   * @todo get imports working between server and frontend packages
   */
  const userOnboardInput = z.object({
    displayName: z
      .string({ required_error: LL.onboarding.errors.nameRequired() })
      .trim()
      .min(2, { message: LL.onboarding.errors.displayNameTooShort({ n: 2 }) })
      .max(50, {
        message: LL.onboarding.errors.displayNameTooLong({ n: 50 }),
      })
      .regex(/^[a-zA-Z\-'\s]+$/, {
        message: LL.onboarding.errors.invalidCharacters(),
      }),
    workspaceName: z
      .string({ required_error: LL.onboarding.errors.workspaceNameRequired() })
      .trim()
      .min(2, { message: LL.onboarding.errors.workspaceNameTooShort({ n: 2 }) })
      .max(50, {
        message: LL.onboarding.errors.workspaceNameTooLong({ n: 50 }),
      })
      .regex(/^[a-zA-Z0-9\-'\s]+$/, {
        message: LL.onboarding.errors.invalidCharactersWorkspace(),
      }),
  });

  const defaultValues = {
    displayName: '',
    workspaceName:
      userAndOrg.data?.organisation.name === 'My Organisation'
        ? ''
        : (userAndOrg.data?.organisation.name ?? ''),
  };

  const {
    register,
    handleSubmit,
    trigger,
    watch,
    getValues,
    formState: { errors, isSubmitting, isValid },
  } = useForm<UserOnboardInputV3>({
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: zodResolver(userOnboardInput),
  });

  // Hiding this for now
  // const mapToIndustry = (input: UserIndustry): string | null => {
  //   switch (input) {
  //     case 'app':
  //       return 'App / Software';
  //     case 'agency':
  //       return null;
  //     case 'beauty':
  //       return 'Beauty / Personal Care';
  //     case 'clothing':
  //       return 'Fashion';
  //     case 'consumerGoods':
  //       return 'Consumer Goods';
  //     case 'education':
  //       return 'Education';
  //     case 'realEstate':
  //       return 'Real Estate';
  //     case 'electronics':
  //       return 'Electronics';
  //     case 'entertainment':
  //       return 'Entertainment';
  //     case 'finance':
  //       return 'Finance';
  //     case 'fitness':
  //       return 'Fitness';
  //     case 'foodBeverage':
  //       return 'Food / Beverage';
  //     case 'games':
  //       return 'Gaming';
  //     case 'healthcare':
  //       return 'Health & Wellness';
  //     case 'home':
  //       return 'Home / Garden';
  //     case 'kids':
  //       return 'Kids';
  //     case 'news':
  //       return 'News / Entertainment';
  //     case 'other':
  //       return 'Other';
  //     case 'sportsOutdoor':
  //       return 'Sports / Outdoor';
  //     case 'services':
  //       return 'Services';
  //     case 'pets':
  //       return 'Pets';
  //     case 'politics':
  //       return null;
  //     case 'transport':
  //       return 'Transport';
  //     case 'travel':
  //       return 'Travel';
  //     case 'travelAccessories':
  //       return 'Travel Accessories';
  //     default:
  //       input satisfies never;
  //       throw new Error('input', input);
  //   }
  // };

  const onSubmit = handleSubmit((data: UserOnboardInputV3) => {
    onboard.mutate(data, {
      onSuccess: () => {
        void recordEvent({
          action: `Submit Successful${bypassAdConnect ? ' - Bypassed Ad Connect' : ''}`,
          target: 'Onboarding',
          metadata: {
            ...data,
          },
        });
        if (bypassAdConnect) {
          void finishOnboarding();
        } else {
          void nextStep();
        }
      },
      onError: (err, opts) => {
        captureException(err, { contexts: { opts } });
      },
    });
  });

  async function finishOnboarding() {
    void recordEvent({
      action: 'Finish Onboarding From Success Screen',
      target: 'Onboarding',
    });
    void trpcUtils.user.getUserAndOrganisation.invalidate();
    void trpcUtils.insightsAccounts.getManyAdAccounts.invalidate();
    void trpcUtils.insightsAccounts.getAdAccount.invalidate();
    void trpcUtils.integrations.getIntegrations.invalidate();
    setShow(false);
    navigate('insights');
  }

  const isFieldValid = useCallback(
    (field: OnboardingField): boolean => {
      const fieldSchema = userOnboardInput.pick({
        [field]: true,
      } as Record<OnboardingField, true>);

      const parse = fieldSchema.safeParse({
        [field]: getValues(field),
      });

      return parse.success;
    },
    [getValues, userOnboardInput]
  );

  async function nextStep() {
    const fieldsToValidate = firstStepFields;

    const isValid = await trigger(fieldsToValidate, { shouldFocus: true });

    if (isValid) {
      direction.current = 1;
      setStep(step + 1);
    }
  }

  function previousStep() {
    direction.current = -1;
    setStep(step - 1);
  }

  const hasEditPermission =
    userAndOrg.data?.role != null &&
    ['wizard', 'superuser', 'owner', 'admin', 'editor'].includes(
      userAndOrg.data.role
    );

  const stepAnimationProps: HTMLMotionProps<'div'> = {
    initial: { x: direction.current * 30, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    transition: {
      duration: 0.2,
      bounce: 0,
    },
  };

  watch();
  const stepsPercentages = (() => {
    const percentages = [10, 0, 0, 0];
    for (let i = 0; i < percentages.length; i++) {
      // Fill completed steps with 100
      if (step > i + 1) {
        percentages[i] = 100;
      }
      // Initialize current step with 10
      if (step === i + 1) {
        percentages[i] = 10;
      }
    }

    switch (step) {
      case 1: {
        let validFieldsCount = 0;
        for (const field of firstStepFields) {
          if (isFieldValid(field)) {
            validFieldsCount++;
          }
        }
        percentages[0] =
          (validFieldsCount / firstStepFields.length) * 100 || 10;
        break;
      }
      case 2: {
        if (
          updateFacebookIntegration.isLoading ||
          connectTikTokIntegration.isLoading
        ) {
          percentages[1] = 50;
        }
        break;
      }
      case 3: {
        if (selectedAdAccountIds.length > 0) {
          percentages[2] = 100;
        }
        break;
      }
    }

    return percentages;
  })();

  useEffect(() => {
    if (step === 2 && facebookAuthState.status === 'success') {
      void nextStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facebookAuthState.status]);

  useEffect(() => {
    if (
      step === 3 &&
      selectedPlatform === 'tiktok' &&
      !tikTokAdvertiserAccounts.data?.advertiserAccounts.length
    ) {
      toast.warn(LL.insights.onboarding.pleaseConnectTikTok());
      previousStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlatform, tikTokAdvertiserAccounts.data, LL, step]);

  const showLoader =
    facebookAuthState.status === 'login' ||
    facebookAuthState.status === 'pending' ||
    updateFacebookIntegration.isLoading ||
    connectTikTokIntegration.isLoading ||
    tikTokAuthState === 'login';

  return (
    <BaseModal
      ref={modalRef}
      show={show}
      onClose={null}
      afterClose={onComplete}
      modalClassName="bg-white rounded-lg sm:w-auto sm:m-0 text-primary"
      overlayClassName="overflow-y-hidden"
      dialogClassName="w-full sm:w-[478px] p-6 flex flex-col gap-6 relative overflow-y-auto"
    >
      {step !== 6 && (
        <SegmentedProgressBar
          className="mb-4"
          stepsPercentages={stepsPercentages}
        />
      )}
      <form onSubmit={onSubmit}>
        {/* Step 1 */}
        {step === 1 && (
          <motion.div {...stepAnimationProps} className="space-y-6">
            <div className="text-center">
              <h1 className="text-pretty text-lg font-semibold leading-8">
                {LL.onboarding.step1Title()}
              </h1>
              <p className="text-sm text-[#917DD2]">
                {LL.onboarding.step1Description()}
              </p>
            </div>
            <div className="space-y-4">
              <Input
                {...register('displayName')}
                error={errors.displayName?.message}
                disabled={isSubmitting}
                label={LL.onboarding.displayName.label()}
                placeholder={LL.onboarding.displayName.placeholder()}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
              />
              <Input
                {...register('workspaceName')}
                error={errors.workspaceName?.message}
                disabled={
                  !hasEditPermission || isSubmitting || isNewInvitedUser
                }
                label={LL.onboarding.workspaceName.label()}
                placeholder={LL.onboarding.workspaceName.placeholder()}
              />
            </div>
            <AriaButton
              onPress={nextStep}
              className="mt-4 w-full"
              isDisabled={
                !firstStepFields.every((field) => isFieldValid(field))
              }
              loading={isSubmitting || onboard.isLoading}
            >
              {LL.continue()}
            </AriaButton>
          </motion.div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <motion.div {...stepAnimationProps} className="space-y-6">
            <div className="text-center">
              <h1 className="text-pretty text-2xl font-semibold leading-8">
                {LL.onboarding.step2Title()}
              </h1>
              <p className="text-sm text-[#917DD2]">
                {LL.onboarding.step2Description()}
              </p>
            </div>
            <div className="space-y-3">
              <h2 className="text-sm font-semibold">
                {LL.insights.onboarding.selectPlatform()}
              </h2>
              <div className="space-y-2">
                <AriaButton
                  variant="custom"
                  className="group h-fit w-full justify-start rounded-lg border border-solid border-purple-200 !p-3 hover:bg-purple-100 disabled:opacity-50"
                  onPress={() => {
                    setSelectedPlatform('facebook');
                    void authenticateFacebook();
                  }}
                >
                  <div className="rounded-lg border border-solid border-secondary/80 bg-white p-2">
                    <MetaLogo className="size-6" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-semibold">Meta</h3>
                    <p className="text-xs font-medium text-[#917DD2]">
                      {LL.insights.onboarding.connectMeta()}
                    </p>
                  </div>
                  <ArrowRight className="ml-auto size-5 -translate-x-px opacity-50 transition-transform group-hover:translate-x-0 group-hover:opacity-100" />
                </AriaButton>
                <TikTokConnect
                  variant="custom"
                  className="group h-fit w-full justify-start rounded-lg border border-solid border-purple-200 !p-3 hover:bg-purple-100 disabled:opacity-50"
                  additionalOnPress={() => setTikTokAuthState('login')}
                  onPopupClose={async () => {
                    await tikTokAdvertiserAccounts.refetch();
                    setSelectedPlatform('tiktok');
                    setTikTokAuthState('init');
                    await nextStep();
                  }}
                  icon={null}
                >
                  <div className="rounded-lg border border-solid border-secondary/80 bg-white p-2">
                    <Tiktok className="size-6" />
                  </div>
                  <div className="text-left">
                    <h3 className="text-sm font-semibold">TikTok</h3>
                    <p className="text-xs font-medium text-[#917DD2]">
                      {LL.insights.onboarding.connectTikTok()}
                    </p>
                  </div>
                  <ArrowRight className="ml-auto size-5 -translate-x-px opacity-50 transition-transform group-hover:translate-x-0 group-hover:opacity-100" />
                </TikTokConnect>
                <ComingSoonAdAccountPlatforms />
              </div>

              <div className="relative py-4">
                <hr className="border-primary/10" />
                <span className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm uppercase text-primary/60">
                  {LL.or()}
                </span>
              </div>

              <AriaButton
                onPress={() => {
                  try {
                    const newWindow = window.open(TOUR_LINK, '_blank');
                    if (newWindow) {
                      void recordEvent({
                        action: 'Book Tour Link Clicked',
                        target: 'Upgrade Modal',
                      });
                    } else {
                      // Fallback to regular navigation
                      window.location.href = TOUR_LINK;
                    }
                  } catch (error) {
                    console.error('Failed to open onboarding:', error);
                  }
                }}
                variant="secondary"
                className="w-full"
              >
                Book Onboarding
              </AriaButton>
            </div>

            {/* DEV BUTTON */}
            {process.env.NODE_ENV === 'development' && (
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-primary/50">
                  Only visible in Dev Mode™
                </span>
                <AriaButton
                  htmlType="submit"
                  isDisabled={!isValid}
                  loading={isSubmitting}
                  onPress={() => setBypassAdConnect(true)}
                >
                  Submit & skip
                </AriaButton>
              </div>
            )}
            {facebookAuthState.status === 'error' && (
              <Alert
                className="w-full"
                title={LL.errors.genericTitle()}
                variant="danger"
              >
                {LL.errors.genericWithDetail({
                  detail: facebookAuthState.error.message ?? 'Unknown error',
                })}
              </Alert>
            )}
          </motion.div>
        )}

        {/* Step 3 */}
        {step === 3 && (
          <motion.div {...stepAnimationProps} className="space-y-6">
            <div className="text-center">
              <h1 className="text-pretty text-2xl font-semibold leading-8">
                {LL.onboarding.step3Title()}
              </h1>
              <p className="text-sm text-[#917DD2]">
                {LL.onboarding.step3Description()}
              </p>
            </div>
            {/* Facebook ad accounts list */}
            {selectedPlatform === 'facebook' &&
              facebookAuthState.status === 'success' && (
                <>
                  <InsightsAdAccountSelectorList
                    label={LL.insights.accounts.accountSelect.title()}
                    advertiserAccounts={facebookAuthState.adAccounts}
                    selectedAdvertiserIds={selectedAdAccountIds}
                    setSelectedAdvertiserIds={setSelectedAdAccountIds}
                    platform="facebook"
                  />
                  <AriaButton
                    className="w-full"
                    onPress={() => {
                      const selectedAdAccounts =
                        facebookAuthState.adAccounts.filter((x) =>
                          selectedAdAccountIds.includes(x.accountId)
                        );

                      updateFacebookIntegration.mutate(
                        {
                          fbUserID: facebookAuthState.fbUserID,
                          accessToken: facebookAuthState.accessToken,
                          adAccounts: selectedAdAccounts,
                        },
                        {
                          onSuccess: () => {
                            void recordEvent({
                              action: 'Facebook Ad Accounts Connected',
                              target: 'Onboarding',
                              metadata: {
                                accounts: facebookAuthState.adAccounts
                                  .map(
                                    (adAccount) =>
                                      `${adAccount.accountId}|${adAccount.name}`
                                  )
                                  .join(', '),
                              },
                            });
                            void nextStep();
                          },
                        }
                      );
                    }}
                    isDisabled={selectedAdAccountIds.length === 0}
                    loading={updateFacebookIntegration.isLoading}
                  >
                    {LL.onboarding.connectPages()}
                  </AriaButton>
                </>
              )}

            {/* TikTok ad accounts list */}
            {selectedPlatform === 'tiktok' && tikTokAdvertiserAccounts.data && (
              <>
                <InsightsAdAccountSelectorList
                  label={LL.insights.accounts.accountSelect.title()}
                  advertiserAccounts={tikTokAdvertiserAccounts.data.advertiserAccounts.map(
                    (advertiser) => ({
                      advertiserId: advertiser.advertiser_id,
                      name: advertiser.name,
                    })
                  )}
                  selectedAdvertiserIds={selectedAdAccountIds}
                  setSelectedAdvertiserIds={setSelectedAdAccountIds}
                  platform="tiktok"
                />
                <AriaButton
                  className="w-full"
                  onPress={() => {
                    const selectedAdAccounts =
                      tikTokAdvertiserAccounts.data.advertiserAccounts.filter(
                        (a) => selectedAdAccountIds.includes(a.advertiser_id)
                      );

                    connectTikTokIntegration.mutate(
                      {
                        advertiserIds: selectedAdAccounts.map(
                          (a) => a.advertiser_id
                        ),
                      },
                      {
                        onSuccess: () => {
                          void recordEvent({
                            action: 'TikTok Ad Accounts Connected',
                            target: 'Onboarding',
                            metadata: {
                              accounts: selectedAdAccounts.map((a) => a.name),
                            },
                          });
                          void nextStep();
                        },
                      }
                    );
                  }}
                  isDisabled={selectedAdAccountIds.length === 0}
                  loading={connectTikTokIntegration.isLoading}
                >
                  {LL.onboarding.connectPages()}
                </AriaButton>
              </>
            )}
          </motion.div>
        )}

        {step === 4 && (
          <motion.div {...stepAnimationProps} className="space-y-6">
            <div className="text-center">
              <h1 className="text-pretty text-2xl font-semibold leading-8">
                {LL.onboarding.step4Title()}
              </h1>
              <p className="text-sm text-[#917DD2]">
                {LL.onboarding.step4Description()}
              </p>
            </div>
            <SlackConnect
              className="-mb-2 flex h-fit w-full items-center !justify-start gap-3 rounded-xl border border-solid border-purple-200 bg-white px-4 py-3 hover:border-purple-300 hover:bg-purple-100"
              variant="custom"
              onSuccess={() => submitButtonRef.current?.click()}
              invalidate={false}
              icon={null}
            >
              <div className="rounded-lg border border-solid border-secondary/80 bg-white p-2">
                <SlackColor className="size-6" />
              </div>
              <div className="space-y-0.5">
                <h3 className="text-left text-sm font-semibold">Slack</h3>
                <p className="text-xs text-[#917dd2]">
                  Receive updates directly in your Slack workspace
                </p>
              </div>
            </SlackConnect>
            <ComingSoonReportingPlatforms />
            <AriaButton
              ref={submitButtonRef}
              htmlType="submit"
              variant="text"
              className="w-full !font-medium"
              isDisabled={!isValid}
              loading={isSubmitting || onboard.isLoading}
            >
              {LL.onboarding.setupLater()}
            </AriaButton>
          </motion.div>
        )}

        {step === 5 && (
          <motion.div
            {...stepAnimationProps}
            className="space-y-4 px-6 pb-10 pt-8 text-center"
          >
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="mx-auto w-fit rounded-full bg-green-500/10 p-1.5"
            >
              <CheckCircle className="size-9 text-green-500" />
            </motion.div>
            <div>
              <h1 className="text-pretty text-2xl font-semibold leading-8">
                {LL.onboarding.success.setupComplete()}
              </h1>
              <p className="text-sm text-primary/70">
                {LL.onboarding.success.setupCompleteDescription()}
              </p>
            </div>
            <AriaButton onPress={finishOnboarding} className="!mt-10 w-full">
              {LL.getStarted()}
            </AriaButton>
          </motion.div>
        )}
      </form>

      {/* Loader mask */}
      {showLoader && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-lg bg-white/60 text-sm font-semibold backdrop-blur-[2px]">
          <Spinner className="size-6" />
          {facebookAuthState.status === 'pending' && (
            <p>{LL.insights.onboarding.connectingAccount()}</p>
          )}
          {(updateFacebookIntegration.isLoading ||
            connectTikTokIntegration.isLoading) && (
            <p>{LL.onboarding.connectingPages()}</p>
          )}
        </div>
      )}

      {step === 5 && !showLoader && (
        <Confetti
          width={modalRef.current?.clientWidth}
          height={modalRef.current?.clientHeight}
          gravity={0.2}
          recycle={false}
          numberOfPieces={500}
        />
      )}
    </BaseModal>
  );
};

export default OnboardingModal;
